import React, { useState, useEffect } from "react";
import { Row, Col, Button, Card, Spin, Modal } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import CandidateTestCard from "../components/CandidateTestCard";
import theme from "../theme";
import api from "../services/api";

interface Test {
  id: number;
  name: string;
  price: number;
  industry_name: string;
  industry_color: string;
  question_count: number;
  time_limit: number;
  passing_grade: number;
  description: string;
}

interface TestAssignment {
  id: number;
  test_id: number;
  test_name: string;
  price: number;
  industry_name: string;
  industry_color: string;
  time_limit: number;
  passing_grade: number;
  question_count: number;
  completed_at: string | null;
  started_at: string | null;
  score: number | null;
  description?: string;
  invited_at: string;
  inviter_name?: string | null;
  inviter_email?: string | null;
}

const CandidateDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [tests, setTests] = useState<Test[]>([]);
  const [assignments, setAssignments] = useState<TestAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAssignments, setHasAssignments] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [inProgressAssignment, setInProgressAssignment] = useState<TestAssignment | null>(null);

  useEffect(() => {
    console.log('CandidateDashboard: Component mounted, loading data...');
    fetchTestData();
  }, []);

  const fetchTestData = async () => {
    console.log('CandidateDashboard: Starting to fetch test data...');
    try {
      setLoading(true);
      setDataLoaded(false);

      // First, try to fetch test assignments
      try {
        const assignmentResponse = await api.get("user/test-assignment/");
        console.log('CandidateDashboard: Assignment response:', assignmentResponse.data);
        const assignmentArray: TestAssignment[] = Array.isArray(
          assignmentResponse.data
        )
          ? assignmentResponse.data
          : [assignmentResponse.data];

        if (assignmentArray.length > 0) {
          console.log('CandidateDashboard: Found assignments, showing assignment view');
          // User has assignments, show those
          const assignmentsWithDescription = assignmentArray.map(
            (assignment) => ({
              ...assignment,
              description:
                assignment.description ||
                "No description available.",
            })
          );
          // Sort: ready to take first, then in-progress, then completed.
          // Completed rows are ordered newest-first so the most recent
          // assessment sits at the top of that group.
          assignmentsWithDescription.sort((a, b) => {
            const rank = (x: typeof a) => {
              if (!x.completed_at && !x.started_at) return 0; // ready
              if (!x.completed_at && x.started_at) return 1;  // in-progress
              return 2;                                        // completed
            };
            const rankDiff = rank(a) - rank(b);
            if (rankDiff !== 0) return rankDiff;
            if (rank(a) === 2) {
              const tA = a.completed_at ? new Date(a.completed_at).getTime() : 0;
              const tB = b.completed_at ? new Date(b.completed_at).getTime() : 0;
              return tB - tA; // newest completed_at first
            }
            return 0;
          });
          setAssignments(assignmentsWithDescription);
          setHasAssignments(true);
          setTests([]);
          // Auto-open modal if any assignment is in progress
          const inProgress = assignmentsWithDescription.find(a => !a.completed_at && a.started_at);
          if (inProgress) {
            setInProgressAssignment(inProgress);
            setShowInProgressModal(true);
          }
        } else {
          throw new Error("No assignments found");
        }
      } catch (assignmentError) {
        // No assignments or error fetching assignments, fetch browse assessments instead
        console.log("CandidateDashboard: No assignments found, loading browse assessments");
        const response = await api.get("browse-tests/");
        const testsWithDescription = response.data
          .slice(0, 6)
          .map((test: any) => ({
            ...test,
            description:
              test.description ||
              "No description available.",
          }));
        setTests(testsWithDescription);
        setHasAssignments(false);
        setAssignments([]);
      }
    } catch (error: any) {
      console.error("CandidateDashboard: Failed to load test data:", error);
      setTests([]);
      setAssignments([]);
      setHasAssignments(false);
    }

    // Add minimum loading time to ensure loading screen is visible
    setTimeout(() => {
      console.log('CandidateDashboard: Setting loading to false');
      setLoading(false);
      setDataLoaded(true);
    }, 800);
  };

  const handleViewTest = (testId: number) => {
    navigate(`/test-details?test_id=${testId}`);
  };

  const handleTakeTest = (assignmentId: number) => {
    navigate(`/begin-test?assignment_id=${assignmentId}`);
  };

  const handleViewResult = (assignmentId: number) => {
    navigate(`/candidate/test-result?assignment_id=${assignmentId}`);
  };

  const handleDownloadCertificate = (assignmentId: number) => {
    navigate(`/candidate/ppp?assignment_id=${assignmentId}`);
  };

  const handleBrowseAllTests = () => {
    navigate("/browse-tests");
  };

  return (
    <div style={bg}>
      {loading || !dataLoaded ? (
        <div style={loadingScreenContainer}>
          <Spin
            size="large"
            indicator={<LoadingOutlined style={{ fontSize: 40, color: theme.colors.primary }} spin />}
          />
          <div style={loadingText}>Loading your assessments...</div>
        </div>
      ) : (
        <>
          {/* In-progress modal */}
          <Modal
            open={showInProgressModal}
            onCancel={() => setShowInProgressModal(false)}
            footer={null}
            centered
            width={480}
          >
            <div style={{ textAlign: "center", padding: "16px 8px 8px" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, color: "#1D1F48", marginBottom: 8 }}>
                Assessment In Progress
              </h3>
              <p style={{ color: "#555", fontSize: 14, marginBottom: 24 }}>
                You have an unfinished assessment:<br />
                <strong>{inProgressAssignment?.test_name}</strong>
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <Button onClick={() => setShowInProgressModal(false)}>
                  Continue Later
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setShowInProgressModal(false);
                    if (inProgressAssignment) handleTakeTest(inProgressAssignment.id);
                  }}
                >
                  Resume Assessment
                </Button>
              </div>
            </div>
          </Modal>

          {/* Header */}
          <div style={headerContainer}>
            <div style={creditsContainer}>
              <span style={creditsText}>0 test credits remaining</span>
            </div>
          </div>

          <div style={mainContent}>
            {!hasAssignments && (
              <div>
                <h1 style={heading}>
                  Hello, {user ? `${user.first_name}`.trim() || "Name" : "Name"}
                </h1>

                <Card style={welcomeCard}>
                  <div style={welcomeMessage}>
                    Welcome to TestInvest! We know you must be keen to get started.
                    <br />
                    Have a look at some of our most popular assessments below:
                  </div>
                </Card>
              </div>
            )}

            {/* Test Cards Grid */}
            <Row gutter={[24, 24]} style={cardGrid}>
              {hasAssignments ? (
                // Show assigned tests
                assignments.length === 0 ? (
                  <Col span={24}>
                    <div style={loadingContainer}>No test assignments found.</div>
                  </Col>
                ) : (
                  assignments.map((assignment) => (
                    <Col key={assignment.id} xs={24} sm={12} lg={8}>
                      <CandidateTestCard
                        id={assignment.id}
                        title={assignment.test_name}
                        price={assignment.price}
                        questionCount={assignment.question_count}
                        timeLimit={
                          assignment.time_limit > 0
                            ? `${assignment.time_limit}`
                            : "No time limit"
                        }
                        description={
                          assignment.description ||
                          "No description available."
                        }
                        industryTags={[assignment.industry_name]}
                        industryColor={assignment.industry_color}
                        showCart={false}
                        status={
                          assignment.completed_at
                            ? "completed"
                            : assignment.started_at
                              ? "in-progress"
                              : "ready"
                        }
                        onClick={() => {
                          if (assignment.completed_at) {
                            handleViewResult(assignment.id);
                          } else {
                            handleTakeTest(assignment.id);
                          }
                        }}
                        onViewResults={() => handleViewResult(assignment.id)}
                        onDownloadCertificate={() => handleDownloadCertificate(assignment.id)}
                        inviterName={assignment.inviter_name}
                        inviterEmail={assignment.inviter_email}
                        invitedAt={assignment.invited_at}
                        completedAt={assignment.completed_at}
                      />
                    </Col>
                  ))
                )
              ) : // Show browse tests
                tests.length === 0 ? (
                  <Col span={24}>
                    <div style={loadingContainer}>
                      No assessments available at the moment.
                    </div>
                  </Col>
                ) : (
                  tests.map((test) => (
                    <Col key={test.id} xs={24} sm={12} lg={8}>
                      <CandidateTestCard
                        id={test.id}
                        title={test.name}
                        price={test.price}
                        questionCount={test.question_count}
                        timeLimit={
                          test.time_limit > 0
                            ? `${test.time_limit}`
                            : "No time limit"
                        }
                        description={test.description}
                        industryTags={[test.industry_name]}
                        industryColor={test.industry_color}
                        showCart={false}
                        onClick={() => handleViewTest(test.id)}
                      />
                    </Col>
                  ))
                )}
            </Row>

            {/* Browse Full Library Section - Only show for users without assignments */}
            <div style={browseSection}>
              {!hasAssignments && <div style={orText}>OR</div>}
              <Button
                type="default"
                size="large"
                onClick={handleBrowseAllTests}
                style={browseButton}
              >
                Browse our full assessment library
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const bg: React.CSSProperties = {
  minHeight: "100vh",
  padding: "0",
};

const loadingScreenContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '70vh',
  gap: '20px',
};

const loadingText: React.CSSProperties = {
  fontSize: '16px',
  color: theme.colors.primary,
  fontWeight: 500,
};

const headerContainer: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  padding: "20px 6%",
};

const creditsContainer: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const creditsText: React.CSSProperties = {
  fontSize: "14px",
  color: "#666",
};

const mainContent: React.CSSProperties = {
  padding: "0 6%",
  paddingBottom: "40px",
};

const heading: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: "0 0 24px 0",
  textAlign: "center",
};

const welcomeCard: React.CSSProperties = {
  marginBottom: "40px",
  borderRadius: "12px",
  border: "2px solid #2EEDB0",
  background: "#E8FFF7",
  marginLeft: "auto",
  marginRight: "auto",
  maxWidth: "650px",
};

const welcomeMessage: React.CSSProperties = {
  color: "#2EEDB0",
  textAlign: "center",
  fontSize: "16px",
  lineHeight: "1.5",
  fontWeight: 500,
};

const cardGrid: React.CSSProperties = {
  marginBottom: "60px",
};

const loadingContainer: React.CSSProperties = {
  textAlign: "center",
  padding: "2rem",
  color: "#666",
};

const browseSection: React.CSSProperties = {
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
};

const orText: React.CSSProperties = {
  fontSize: "18px",
  color: "#666",
  fontWeight: 500,
};

const browseButton: React.CSSProperties = {
  borderRadius: "20px",
  padding: "6px 32px",
  height: "auto",
  fontSize: "16px",
  fontWeight: 500,
  background: "#E5EBFA",
  color: "#333",
};

export default CandidateDashboard;
